import { CameraIcon, LightBulbIcon } from "@heroicons/react/24/outline";
import type { PageIssue } from "@/types";

type Props = {
  issue: PageIssue;
};

export default function PageIssueCard({ issue }: Props) {
  return (
    <article
      id={issue.domain_issue_id ? `issue-${issue.domain_issue_id}` : undefined}
      className="scroll-mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
    >
      <div className="mb-3 flex flex-col justify-between gap-2 sm:flex-row sm:items-start">
        <div>
          {issue.heuristic?.name ? (
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{issue.heuristic.name}</p>
          ) : null}
          <h3 className="text-lg font-semibold text-gray-900">{issue.title || "Untitled issue"}</h3>
        </div>
      </div>

      {issue.description ? <p className="text-sm leading-relaxed text-gray-700">{issue.description}</p> : null}

      {issue.suggestion ? (
        <div className="mt-4 rounded-r-md border-l-4 border-gray-300 bg-gray-50 p-3">
          <div className="flex items-start">
            <LightBulbIcon className="mr-2 mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-600">Suggestion</p>
              <p className="text-sm leading-relaxed text-gray-700">{issue.suggestion}</p>
            </div>
          </div>
        </div>
      ) : null}

      {issue.screenshot?.image_url ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-2">
          <div className="mb-2 flex items-center text-sm font-medium text-gray-700">
            <CameraIcon className="mr-2 h-4 w-4" aria-hidden="true" />
            Screenshot evidence
          </div>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <img
              src={issue.screenshot.image_url}
              alt={`Screenshot evidence for ${issue.title || "issue"}`}
              className="h-auto max-h-[667px] w-full object-contain"
              loading="lazy"
            />
          </div>
        </div>
      ) : null}

      {import.meta.env.DEV && issue.audit_url ? (
        <a href={issue.audit_url} className="mt-4 inline-block text-sm font-medium text-primary hover:underline">
          View source audit
        </a>
      ) : null}
    </article>
  );
}
